/**
 * Panels for shared URLs when the viewer cannot see full content.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import UserAvatar from '../Profile/UserAvatar';
import FollowButton from '../Profile/FollowButton';

export function PostLoginWall({ poster }) {
  const u = poster?.user_id
    ? {
        user_id: poster.user_id,
        username: poster.username,
        displayname: poster.displayname,
        profile_pic_url: poster.profile_pic_url,
        default_profile_color: poster.default_profile_color,
      }
    : null;

  return (
    <div
      className="rounded-2xl border border-shadow bg-charcoal/60 backdrop-blur-sm p-6 text-center max-w-md mx-auto"
      data-testid="share-post-login-wall"
    >
      <p className="text-ash mb-4">This post is from a private profile. Log in to continue.</p>
      {u ? (
        <div className="flex flex-col items-center gap-2">
          <UserAvatar user={u} className="w-16 h-16 rounded-full" emojiClassName="text-2xl" />
          <p className="text-silver text-sm">
            @{u.username}
            {u.displayname ? ` · ${u.displayname}` : ''}
          </p>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-3 justify-center mt-6">
        <Link
          to="/"
          className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-ether text-void text-sm font-medium hover:opacity-90 transition-zen"
        >
          Log in
        </Link>
        <Link
          to="/register"
          className="inline-flex items-center justify-center px-4 py-2 rounded-xl border border-shadow text-ash text-sm font-medium hover:border-ether hover:text-ether transition-colors"
        >
          Sign up
        </Link>
      </div>
    </div>
  );
}

export function PostFollowWall({ poster, initialFollowStatus, onFollowAccepted }) {
  const u = poster?.user_id
    ? {
        user_id: poster.user_id,
        username: poster.username,
        displayname: poster.displayname,
        profile_pic_url: poster.profile_pic_url,
        default_profile_color: poster.default_profile_color,
      }
    : null;

  return (
    <div
      className="rounded-2xl border border-shadow bg-charcoal/60 backdrop-blur-sm p-6 text-center max-w-md mx-auto"
      data-testid="share-post-follow-wall"
    >
      <p className="text-ash mb-2">This post is from a private account.</p>
      <p className="text-silver text-sm mb-4">
        Follow {u?.username ? `@${u.username}` : 'this user'} to see their posts.
      </p>
      {u ? (
        <div className="flex flex-col items-center gap-3">
          <UserAvatar user={u} className="w-16 h-16 rounded-full" emojiClassName="text-2xl" />
          <FollowButton
            targetUserId={u.user_id}
            initialStatus={initialFollowStatus}
            onStatusChange={(s) => {
              if (s === 'accepted') onFollowAccepted?.();
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

export function ProfileLoginWall({ poster }) {
  const u = poster?.user_id
    ? {
        user_id: poster.user_id,
        username: poster.username,
        displayname: poster.displayname,
        profile_pic_url: poster.profile_pic_url,
        default_profile_color: poster.default_profile_color,
      }
    : null;

  return (
    <div
      className="rounded-2xl border border-shadow bg-charcoal/60 backdrop-blur-sm p-6 text-center max-w-md mx-auto"
      data-testid="share-profile-login-wall"
    >
      {u ? (
        <div className="flex flex-col items-center gap-2">
          <UserAvatar user={u} className="w-16 h-16 rounded-full" emojiClassName="text-2xl" />
          <h2 className="font-display text-lg font-bold text-ash">
            @{u.username} | {u.displayname || u.username}
          </h2>
          <p className="text-silver text-sm mt-2">Log in to see posts and activity.</p>
        </div>
      ) : (
        <p className="text-ash mb-4">This profile is private.</p>
      )}
      <div className="flex flex-wrap gap-3 justify-center mt-6">
        <Link
          to="/"
          className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-ether text-void text-sm font-medium hover:opacity-90 transition-zen"
        >
          Log in
        </Link>
      </div>
    </div>
  );
}

export function ProfileFollowWall({ poster, initialFollowStatus, onFollowAccepted }) {
  return (
    <PostFollowWall
      poster={poster}
      initialFollowStatus={initialFollowStatus}
      onFollowAccepted={onFollowAccepted}
    />
  );
}

export function LoginForCommentsNotice() {
  return (
    <div
      className="border-t border-shadow mt-2 py-4 text-center"
      data-testid="login-for-comments-notice"
    >
      <p className="text-silver text-sm">Log in to see and add comments.</p>
      <Link
        to="/"
        className="mt-3 inline-flex items-center justify-center px-4 py-2 rounded-xl bg-ether text-void text-sm font-medium hover:opacity-90 transition-opacity"
      >
        Log in
      </Link>
    </div>
  );
}

export function LoginForFeedNotice() {
  return (
    <div
      className="rounded-2xl border border-shadow bg-charcoal/40 p-6 text-center mt-4"
      data-testid="login-for-feed-notice"
    >
      <p className="text-ash text-sm mb-4">Log in to see posts and activity on this profile.</p>
      <div className="flex flex-wrap gap-3 justify-center">
        <Link
          to="/"
          className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-ether text-void text-sm font-medium hover:opacity-90 transition-zen"
        >
          Log in
        </Link>
        <Link
          to="/register"
          className="inline-flex items-center justify-center px-4 py-2 rounded-xl border border-shadow text-ash text-sm font-medium hover:border-ether hover:text-ether transition-colors"
        >
          Sign up
        </Link>
      </div>
    </div>
  );
}
